import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './SideBar.css';

const SideBar = (
    { 
        menuItems=[], 
        logo, 
        cardData=[], 
        footerContent, 
        defaultCollapsed = true, 
        webUrl='',
        userRole,
    }) => {
    const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
    const [openSubmenus, setOpenSubmenus] = useState({});
    const [activeItem, setActiveItem] = useState(null);
    const [currentCardIndex, setCurrentCardIndex] = useState(0);

    const sidebarRef = useRef(null);
    const navigate = useNavigate();

    const toggleSidebar = () => setIsCollapsed(prev => !prev);

    const toggleSubmenu = (id) => {
        setOpenSubmenus(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // Handle click outside the sidebar
    const handleClickOutside = (event) => {
        if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
            setIsCollapsed(true);
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleItemClick = (path, id) => {
        navigate(path);
        setActiveItem(id);
        setIsCollapsed(true);
    };

    const currentCard = cardData[currentCardIndex];

    // Card rotation effect
    useEffect(() => {
        const cardRotationInterval = setInterval(() => {
        setCurrentCardIndex((prevIndex) => (prevIndex + 1) % cardData.length);
        }, 20000); // 20 seconds

        return () => clearInterval(cardRotationInterval);
    }, [cardData.length]);

    return (
        <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`} ref={sidebarRef}>
            <div className="sidebar-header">
                {isCollapsed ? 
                    <button className="collapse-btn" onClick={toggleSidebar}>
                        ☰
                    </button>
                    :
                    <button className="sidebar-close-btn" onClick={toggleSidebar}>
                        X
                    </button>
                }
                {!isCollapsed && logo && <img src={logo} alt="Logo" className="sidebar-logo" />}
            </div>

            <nav className={`sidebar-nav ${isCollapsed ? "collapse" : "expand"}`}>
                <ul className="menu-list">
                    {menuItems.map(item => {
                         // Check if user role is allowed for this menu item
                        if (item.allowedRoles && !item.allowedRoles.includes(userRole)) return null;

                        return (
                        <li 
                            key={item.id} 
                            className="menu-item"
                            onMouseEnter={() => isCollapsed && setOpenSubmenus({ [item.id]: true })}
                            onMouseLeave={() => isCollapsed && setOpenSubmenus({ [item.id]: false })}
                        >
                            {item.submenu ? (
                                <>
                                    <button
                                        className={`menu-button ${activeItem === item.id ? 'active' : ""}`}
                                        onClick={() => !isCollapsed && toggleSubmenu(item.id)}
                                        title={isCollapsed ? item.label : ''}
                                    >
                                        <span className={`menu-icon ${isCollapsed ? 'collapse' : 'expand'}`}>
                                            {item.icon}
                                        </span>
                                        {!isCollapsed && <span className="menu-label">{item.label}</span>}
                                        {/* {!isCollapsed && <FaChevronDown className="submenu-icon" />} */}
                                        {!isCollapsed && (
                                            <i
                                            className={`fa-solid fa-angle-down submenu-icon ${
                                                openSubmenus[item.id] ? 'open' : ''
                                            }`}
                                            ></i>
                                        )}

                                    </button>

                                    {/* Expanded: inline submenu */}
                                    {openSubmenus[item.id] && !isCollapsed && (
                                        <ul className="submenu">
                                            {item.submenu.map(sub => {
                                            // Check if user role is allowed for this sub menu item
                                            if (sub.allowedRoles && !sub.allowedRoles.includes(userRole)) return null;

                                            return (
                                                <li key={sub.id}>
                                                    <button 
                                                        className={`menu-button ${activeItem === sub.id ? 'active' : ""}`}
                                                        onClick={() => handleItemClick(sub.path, sub.id)}
                                                    >
                                                        <span className="menu-icon">
                                                            {sub.icon}
                                                        </span>
                                                        <span className="menu-label">{sub.label}</span>
                                                    </button>
                                                </li>
                                            );
                                            })}
                                        </ul>
                                    )}

                                    {/* Collapsed: floating submenu */}
                                    {openSubmenus[item.id] && isCollapsed && (
                                        <div className="submenu-tooltip">
                                            {item.submenu.map(sub => {
                                                // Check if user role is allowed for this sub menu item
                                                if (sub.allowedRoles && !sub.allowedRoles.includes(userRole)) return null;

                                                return(
                                                    <button
                                                        key={sub.id}
                                                        className={`menu-button ${activeItem === sub.id ? 'active' : ""}`}
                                                        onClick={() => handleItemClick(sub.path, sub.id)}
                                                    >
                                                        <span className="menu-icon">
                                                            {sub.icon}
                                                        </span>
                                                        {sub.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <button 
                                    className={`menu-button ${activeItem === item.id ? 'active' : ""}`} 
                                    title={isCollapsed ? item.label : ''} 
                                    onClick={() => handleItemClick(item.path, item.id)}
                                >
                                    <span className={`menu-icon ${isCollapsed ? 'collapse' : 'expand'}`}>
                                        {item.icon}
                                    </span>
                                    {!isCollapsed && <span className="menu-label">{item.label}</span>}
                                </button>
                            )}
                        </li>

                        );
                    })}
                </ul>
            </nav>

            {footerContent && (
                // Only show logo when collapsed
                isCollapsed ?
                (<div className="sidebar-footer">
                    {footerContent}
                </div>)
                :
                // Updated card container - now shows only one card
                <div className='sidebar-ul-card-container'>
                    <li className='sidebar-ul-card-li' key={currentCard.id}>
                        <div className='sidebar-ul-card'>
                            <div className='sidebar-ul-card-body'>
                                <img src={currentCard.image} alt={currentCard.text} className='sidebar-ul-card-img' />
                                <p className='card-text'>{currentCard.text}</p>
                                <a href={webUrl} target="_blank" rel="noreferrer" className='sidebar-ul-card-btn-container'>
                                <button className='card-btn'>Learn More</button>
                                </a>
                            </div>
                        </div>
                    </li>
                </div>
            )}
        </div>
    );
};

export default SideBar;
